/**
 * Time Service — Single Source of Truth for All Session-Time Logic
 *
 * Every time/date concern in the app routes through this module:
 *   - Trading-day boundaries (6pm ET roll on `trade.date`)
 *   - Journal "today" rollover (5:15pm ET, 45-min prep window before market reopens)
 *   - Date-string arithmetic and comparisons (string in, string out)
 *   - HH:MM time math (entryTime/exitTime/event.time)
 *   - Cross-timezone conversion (source TZ → ET; ET → UTC for event display)
 *
 * Invariants this module enforces by being the only path callers use:
 *   1. `trade.date` is always an ET-anchored YYYY-MM-DD trading-day label.
 *   2. `trade.entryTime` and `trade.exitTime` are always HH:MM in ET.
 *   3. Calendar arithmetic operates on date strings, never on Date objects.
 *   4. Session boundaries reference the constants below — never literals.
 *
 * The CME futures session runs 6pm ET → 5pm ET the next day, with a daily
 * maintenance break 5pm–6pm ET. We anchor all session math to ET regardless
 * of where the app runs (CME's session is fixed in ET, not the user's local
 * clock). DST is handled transparently via Intl.DateTimeFormat.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

export const SESSION_TZ = 'America/New_York';

// Trade-date roll: any fill at or after this hour ET belongs to the next day's
// session. CME daily maintenance break ends at 6pm ET (= 5pm CT). Same instant.
export const TRADE_DAY_BOUNDARY_HOUR = 18;

// Journal "today" rollover: 45 minutes after session close so the trader can
// prep the next session's plan during the closed hour before market reopens.
export const JOURNAL_ROLLOVER_HOUR = 17;
export const JOURNAL_ROLLOVER_MINUTE = 15;

export const SESSION_OPEN_HOUR = 18;   // 6pm ET — market reopens
export const SESSION_CLOSE_HOUR = 17;  // 5pm ET — market closes

export const TIMEZONE_OPTIONS = [
  { value: 'Eastern',   label: 'Eastern (ET)' },
  { value: 'Central',   label: 'Central (CT)' },
  { value: 'Mountain',  label: 'Mountain (MT)' },
  { value: 'Pacific',   label: 'Pacific (PT)' },
  { value: 'UTC',       label: 'UTC/GMT' },
  { value: 'Europe',    label: 'Central European (CET)' },
  { value: 'Japan',     label: 'Japan (JST)' },
  { value: 'Australia', label: 'Australian Eastern (AEST)' },
];

export const IANA_BY_FRIENDLY = {
  Eastern:   'America/New_York',
  Central:   'America/Chicago',
  Mountain:  'America/Denver',
  Pacific:   'America/Los_Angeles',
  UTC:       'UTC',
  Europe:    'Europe/Berlin',
  Japan:     'Asia/Tokyo',
  Australia: 'Australia/Sydney',
};

// ─────────────────────────────────────────────────────────────────────────────
// Core ET extraction
// ─────────────────────────────────────────────────────────────────────────────

const ET_FORMATTER = (() => {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: SESSION_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return null;
  }
})();

/**
 * Extract calendar/clock parts in Eastern Time from any Date.
 * Returns { year, month, day, hour, minute } where year/month/day are
 * zero-padded strings and hour/minute are integers.
 *
 * Falls back to local-time parts only if Intl/tzdb is unavailable —
 * in that case the host clock must already be on ET for correctness.
 */
export const getEtParts = (date) => {
  if (ET_FORMATTER) {
    const parts = ET_FORMATTER.formatToParts(date);
    const get = (type) => parts.find(p => p.type === type)?.value || '';
    let hour = parseInt(get('hour'), 10);
    if (hour === 24) hour = 0; // some Intl impls emit "24" for midnight
    return {
      year: get('year'),
      month: get('month'),
      day: get('day'),
      hour,
      minute: parseInt(get('minute'), 10),
    };
  }
  return {
    year: String(date.getFullYear()),
    month: String(date.getMonth() + 1).padStart(2, '0'),
    day: String(date.getDate()).padStart(2, '0'),
    hour: date.getHours(),
    minute: date.getMinutes(),
  };
};

/** ET-anchored `YYYY-MM-DD` date string for any Date. */
export const formatEtDate = (date) => {
  const { year, month, day } = getEtParts(date);
  return `${year}-${month}-${day}`;
};

/**
 * ET-anchored `HH:MM` time string for any timestamp.
 * This is the canonical formatter for `trade.entryTime` / `trade.exitTime` —
 * those fields are always stored in ET regardless of where the import or
 * sync pipeline runs. Returns '' for null/invalid inputs.
 */
export const formatEtTime = (timestamp) => {
  if (timestamp == null || timestamp === '') return '';
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (isNaN(date.getTime())) return '';
  const { hour, minute } = getEtParts(date);
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

// Internal: build a YYYY-MM-DD string from numeric year/month/day fields.
const buildDateStr = (y, m, d) =>
  `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

// ─────────────────────────────────────────────────────────────────────────────
// Trading day primitives
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Trading-day label for any timestamp, anchored to ET.
 * A fill at 7pm ET Sunday returns Monday's date. Cross-timezone safe.
 *
 * @param {string|number|Date} timestamp - ISO string, ms epoch, or Date
 * @returns {string|null} YYYY-MM-DD or null if invalid
 */
export const getTradingDay = (timestamp) => {
  if (timestamp == null) return null;
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (isNaN(date.getTime())) return null;
  const { year, month, day, hour } = getEtParts(date);
  if (hour >= TRADE_DAY_BOUNDARY_HOUR) {
    return addDaysToDateStr(`${year}-${month}-${day}`, 1);
  }
  return `${year}-${month}-${day}`;
};

/**
 * Current trading day — what the journal calls "today".
 * Rolls 45 minutes after session close (5:15pm ET) so the trader has
 * prep time during the closed hour before market reopens at 6pm ET.
 *
 * @returns {string} YYYY-MM-DD trading day in ET
 */
export const getCurrentTradingDay = () => {
  const { year, month, day, hour, minute } = getEtParts(new Date());
  const past515 =
    hour > JOURNAL_ROLLOVER_HOUR ||
    (hour === JOURNAL_ROLLOVER_HOUR && minute >= JOURNAL_ROLLOVER_MINUTE);
  if (past515) {
    return addDaysToDateStr(`${year}-${month}-${day}`, 1);
  }
  return `${year}-${month}-${day}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// Boundary application (TZ-aware)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Apply the 6pm ET trading-day boundary to a (calendarDate, entryTime) pair,
 * where entryTime is the wall-clock entry time IN `sourceTz`. If the source
 * timezone differs from ET, the entry time is converted to ET first, then
 * the boundary check is applied.
 *
 * Use cases:
 *   - CSV import where entryTime is in user's NinjaTrader-configured TZ
 *   - Manual trade entry where the user typed a time
 *   - JournifySync where timestamps come from the broker stream
 *
 * @param {string} calendarDate - YYYY-MM-DD in `sourceTz`
 * @param {string} entryTime    - HH:MM in `sourceTz`
 * @param {string} sourceTz     - IANA timezone of the entry time (default: ET)
 * @returns {string} YYYY-MM-DD trading day in ET
 */
export const applyTradingDayBoundary = (calendarDate, entryTime, sourceTz = SESSION_TZ) => {
  if (!calendarDate || !entryTime) return calendarDate;

  // Parse HH:MM (the source-tz wall clock entry time)
  const sourceMins = parseHM(entryTime);
  if (sourceMins == null) return calendarDate;

  // Convert source-tz date+time to a UTC instant, then re-extract in ET.
  // Build the wall-clock as if it were UTC, then apply the actual offset for
  // sourceTz on that calendar day to get the real UTC instant.
  const [y, m, d] = calendarDate.split('-').map(n => parseInt(n, 10));
  if (!y || !m || !d) return calendarDate;
  const sourceHour = Math.floor(sourceMins / 60);
  const sourceMinute = sourceMins % 60;

  const utcInstant = wallTimeInTzToUtcMs(sourceTz, y, m, d, sourceHour, sourceMinute);
  if (utcInstant == null) return calendarDate;

  return getTradingDay(new Date(utcInstant));
};

// Internal: given a wall-clock time + IANA tz, return the UTC ms instant.
// Uses Intl.DateTimeFormat to discover the offset for that wall time.
const wallTimeInTzToUtcMs = (tz, year, month, day, hour, minute) => {
  // First guess: treat the wall time as if it were UTC.
  const guessUtc = Date.UTC(year, month - 1, day, hour, minute);
  // Ask Intl what wall time the tz sees for that UTC instant — the difference
  // between that and our intended wall time IS the tz offset.
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(new Date(guessUtc));
    const get = (type) => parts.find(p => p.type === type)?.value;
    let tzHour = parseInt(get('hour'), 10);
    if (tzHour === 24) tzHour = 0;
    const tzMinute = parseInt(get('minute'), 10);
    const tzDay = parseInt(get('day'), 10);
    const tzMonth = parseInt(get('month'), 10);
    const tzYear = parseInt(get('year'), 10);
    // Compute drift in minutes between intended wall and what Intl saw
    const intendedAsMins = (((Date.UTC(year, month - 1, day) - Date.UTC(year, 0, 1)) / 86400000) * 1440) + hour * 60 + minute;
    const tzAsMins = (((Date.UTC(tzYear, tzMonth - 1, tzDay) - Date.UTC(year, 0, 1)) / 86400000) * 1440) + tzHour * 60 + tzMinute;
    const offsetMins = intendedAsMins - tzAsMins;
    return guessUtc + offsetMins * 60000;
  } catch {
    return guessUtc; // fallback: treat as UTC
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Execution-instant for trade sorting
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Wall-clock execution instant for a trade, given its trading-day label and
 * entry time. Used for sorting trades within a session correctly.
 *
 * Rationale: a trade with date=Monday entryTime=19:00 actually executed at
 * 7pm ET Sunday (the prior calendar evening). A trade with date=Monday
 * entryTime=09:30 executed at 9:30am ET Monday. Sorted ascending by the
 * returned ms, the first trade comes BEFORE the second, matching what
 * actually happened in the market.
 *
 * Returns 0 (or trade.date noon ET) when entryTime is missing.
 *
 * @param {string} tradingDate - YYYY-MM-DD trading day (ET)
 * @param {string} entryTime   - HH:MM (ET)
 * @returns {number} milliseconds since epoch
 */
export const getExecutionInstantMs = (tradingDate, entryTime) => {
  if (!tradingDate) return 0;
  const [y, m, d] = tradingDate.split('-').map(n => parseInt(n, 10));
  if (!y || !m || !d) return 0;

  if (!entryTime) {
    // No entry time — anchor at noon ET of the trading day for stable sort.
    return wallTimeInTzToUtcMs(SESSION_TZ, y, m, d, 12, 0);
  }

  const mins = parseHM(entryTime);
  if (mins == null) {
    return wallTimeInTzToUtcMs(SESSION_TZ, y, m, d, 12, 0);
  }
  const hour = Math.floor(mins / 60);
  const minute = mins % 60;

  // If entry hour is the evening session (≥ 18), the actual calendar day is
  // the day BEFORE the trading-day label.
  if (hour >= TRADE_DAY_BOUNDARY_HOUR) {
    const prevDay = addDaysToDateStr(tradingDate, -1);
    const [py, pm, pd] = prevDay.split('-').map(n => parseInt(n, 10));
    return wallTimeInTzToUtcMs(SESSION_TZ, py, pm, pd, hour, minute);
  }
  return wallTimeInTzToUtcMs(SESSION_TZ, y, m, d, hour, minute);
};

// ─────────────────────────────────────────────────────────────────────────────
// Date string arithmetic
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Add `delta` days to a YYYY-MM-DD string. Handles month/year/leap rollovers.
 * Returns YYYY-MM-DD.
 */
export const addDaysToDateStr = (dateStr, delta) => {
  if (!dateStr) return dateStr;
  const [y, m, d] = dateStr.split('-').map(n => parseInt(n, 10));
  if (!y || !m || !d) return dateStr;
  // Use the local-Date constructor purely for date-field arithmetic. The result's
  // local timezone is irrelevant — we only read year/month/day fields, which are
  // unaffected by the host's offset.
  const dt = new Date(y, m - 1, d + delta);
  return buildDateStr(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
};

/**
 * Days between two YYYY-MM-DD strings (b - a). Positive when b is later.
 * Returns null on invalid input.
 */
export const dateStrDiff = (a, b) => {
  if (!a || !b) return null;
  const [ay, am, ad] = a.split('-').map(n => parseInt(n, 10));
  const [by, bm, bd] = b.split('-').map(n => parseInt(n, 10));
  if (!ay || !am || !ad || !by || !bm || !bd) return null;
  // Use UTC-anchored Dates so DST transitions don't shift the diff.
  const aMs = Date.UTC(ay, am - 1, ad);
  const bMs = Date.UTC(by, bm - 1, bd);
  return Math.round((bMs - aMs) / 86400000);
};

/**
 * ISO week-start (Monday) for a YYYY-MM-DD date. Stable across years.
 */
export const weekStartOfDateStr = (dateStr) => {
  if (!dateStr) return dateStr;
  const [y, m, d] = dateStr.split('-').map(n => parseInt(n, 10));
  if (!y || !m || !d) return dateStr;
  const dt = new Date(y, m - 1, d);
  const day = dt.getDay(); // Sun=0..Sat=6
  const offset = day === 0 ? -6 : 1 - day; // shift to Monday
  dt.setDate(dt.getDate() + offset);
  return buildDateStr(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
};

/** YYYY-MM prefix from numeric year + 1-based month. */
export const monthPrefixOf = (year, monthOneBased) =>
  `${year}-${String(monthOneBased).padStart(2, '0')}`;

// ─────────────────────────────────────────────────────────────────────────────
// Trade-date predicates (the "always string-compare" rule)
// ─────────────────────────────────────────────────────────────────────────────

/** Inclusive range check on YYYY-MM-DD strings. */
export const tradeDateInRange = (tradeDate, startStr, endStr) => {
  if (!tradeDate) return false;
  if (startStr && tradeDate < startStr) return false;
  if (endStr && tradeDate > endStr) return false;
  return true;
};

export const tradeDateAfter = (tradeDate, otherDateStr) =>
  !!tradeDate && !!otherDateStr && tradeDate > otherDateStr;

export const tradeDateOnOrAfter = (tradeDate, otherDateStr) =>
  !!tradeDate && !!otherDateStr && tradeDate >= otherDateStr;

export const tradeDateBefore = (tradeDate, otherDateStr) =>
  !!tradeDate && !!otherDateStr && tradeDate < otherDateStr;

export const tradeDateOnOrBefore = (tradeDate, otherDateStr) =>
  !!tradeDate && !!otherDateStr && tradeDate <= otherDateStr;

/**
 * Is `tradeDate` within the last `n` trading days, anchored at `anchor`
 * (defaults to current trading day)? Inclusive on both ends.
 */
export const tradeDateInLastNDays = (tradeDate, n, anchor) => {
  if (!tradeDate || !Number.isFinite(n) || n < 0) return false;
  const a = anchor || getCurrentTradingDay();
  const cutoff = addDaysToDateStr(a, -(n - 1)); // n=7 includes today + prior 6 days
  return tradeDate >= cutoff && tradeDate <= a;
};

export const tradeDateInMonth = (tradeDate, year, monthOneBased) => {
  if (!tradeDate) return false;
  return tradeDate.startsWith(monthPrefixOf(year, monthOneBased));
};

/**
 * Period-based predicate matching the app's filter dropdown:
 *   'all'   — always true
 *   'today' — equal to anchor
 *   'week'  — last 7 days
 *   'month' — last 30 days
 *   '7'|'14'|'30'|'60'|'90'|'180'|'365' — last N days
 */
export const tradeDateInPeriod = (tradeDate, period, anchor) => {
  if (!tradeDate) return false;
  if (!period || period === 'all') return true;
  const a = anchor || getCurrentTradingDay();
  if (period === 'today') return tradeDate === a;
  if (period === 'week')  return tradeDateInLastNDays(tradeDate, 7, a);
  if (period === 'month') return tradeDateInLastNDays(tradeDate, 30, a);
  const n = parseInt(period, 10);
  if (Number.isFinite(n) && n > 0) return tradeDateInLastNDays(tradeDate, n, a);
  return true;
};

// ─────────────────────────────────────────────────────────────────────────────
// HH:MM time math
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Parse a time string into minutes-from-midnight. TZ-agnostic — assumes the
 * caller has already aligned the input to the comparison frame (typically ET).
 *
 * Accepted shapes:
 *   "HH:MM"                       (24h)
 *   "HH:MM:SS"                    (24h, seconds ignored)
 *   "H:MM AM/PM"                  (12h, no date prefix)
 *   "MM/DD/YYYY HH:MM AM/PM"      (12h with date)
 *   "YYYY-MM-DDTHH:MM[:SS]"       (ISO)
 *   "YYYY-MM-DD HH:MM[:SS]"       (space-separated)
 *   Date instance                 (uses local hours/minutes — caller must already be in target frame)
 *
 * @returns {number|null} minutes from midnight, or null on parse failure
 */
export const parseHM = (s) => {
  if (s == null || s === '') return null;
  if (s instanceof Date) return s.getHours() * 60 + s.getMinutes();
  if (typeof s !== 'string') return null;

  const str = s.trim();
  if (!str) return null;

  // ISO with T separator: take part after T
  let core = str.includes('T') ? str.split('T')[1] : str;
  // Date-prefixed forms: take last whitespace-separated segment(s) starting with the time
  if (core === str && /[/-]/.test(str) && str.includes(' ')) {
    const segs = str.split(/\s+/);
    // Find the first segment containing a colon (the HH:MM portion)
    const idx = segs.findIndex(seg => seg.includes(':'));
    if (idx >= 0) {
      core = segs.slice(idx).join(' ');
    }
  }
  if (!core) return null;

  // AM/PM detection at the end
  const periodMatch = core.match(/\s*(AM|PM)\s*$/i);
  const period = periodMatch ? periodMatch[1].toUpperCase() : null;
  const hmCore = period ? core.slice(0, periodMatch.index).trim() : core.trim();

  const parts = hmCore.split(':').map(v => parseInt(v, 10));
  const [h, m] = parts;
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  if (h < 0 || h > 23 || m < 0 || m > 59) {
    // Allow 12 with PM (which means noon) but reject 24+
    if (period && h === 12 && (period === 'AM' || period === 'PM')) {
      // continue — handled below
    } else if (h > 23) {
      return null;
    }
  }

  let hours = h;
  if (period === 'PM' && h !== 12) hours += 12;
  if (period === 'AM' && h === 12) hours = 0;
  if (hours < 0 || hours > 23) return null;
  return hours * 60 + m;
};

/** Format minutes-from-midnight as "HH:MM". */
export const formatHM = (mins) => {
  if (!Number.isFinite(mins)) return '';
  const m = ((Math.round(mins) % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

/** Minute difference between two HH:MM strings (b - a). null on parse failure. */
export const minuteDelta = (a, b) => {
  const ma = parseHM(a);
  const mb = parseHM(b);
  if (ma == null || mb == null) return null;
  return mb - ma;
};

/** Are two HH:MM times within `windowMin` minutes of each other? */
export const isWithinMinutes = (timeA, timeB, windowMin) => {
  const d = minuteDelta(timeA, timeB);
  if (d == null) return false;
  return Math.abs(d) <= windowMin;
};

/**
 * ET market-hour bucket for an entry time.
 *   morning   — before 11:00 ET (open + first push)
 *   midday    — 11:00–14:00 ET (lunch / chop window)
 *   afternoon — 14:00 ET onward
 */
export const timeBucket = (entryTime) => {
  const mins = parseHM(entryTime);
  if (mins == null) return null;
  const hour = Math.floor(mins / 60);
  if (hour < 11) return 'morning';
  if (hour < 14) return 'midday';
  return 'afternoon';
};

/** Hour-of-day integer (0..23) from an entry time. */
export const hourOf = (entryTime) => {
  const mins = parseHM(entryTime);
  return mins == null ? null : Math.floor(mins / 60);
};

/** Humanize an hour: 9 → "9am", 14 → "2pm", 0 → "12am", 12 → "12pm". */
export const humanHour = (hour) => {
  if (hour == null || !Number.isFinite(hour)) return '';
  const h = ((hour % 24) + 24) % 24;
  if (h === 0) return '12am';
  if (h === 12) return '12pm';
  return h > 12 ? `${h - 12}pm` : `${h}am`;
};

// ─────────────────────────────────────────────────────────────────────────────
// Cross-TZ conversion
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Parse an Eastern-Time wall-clock date/time pair into a UTC Date instance.
 * DST-aware via Intl.DateTimeFormat. Used by the economic calendar to take
 * times from the FairEconomy XML feed (always ET) and convert to UTC for
 * subsequent display in the user's preferred TZ.
 *
 * @param {string} dateText - "MM-DD-YYYY"
 * @param {string} timeText - "h:mmam" / "hh:mmpm" (case-insensitive)
 * @returns {Date|null}
 */
export const parseEtToUtc = (dateText, timeText) => {
  if (!dateText || !timeText) return null;

  const dateParts = dateText.split('-').map(s => parseInt(s, 10));
  if (dateParts.length !== 3 || dateParts.some(n => isNaN(n))) return null;
  const [mm, dd, yyyy] = dateParts;

  const m = /^(\d{1,2}):(\d{2})\s*(am|pm)$/i.exec(timeText.trim());
  if (!m) return null;
  let hour = parseInt(m[1], 10);
  const minute = parseInt(m[2], 10);
  const meridiem = m[3].toLowerCase();
  if (hour === 12) hour = 0;
  if (meridiem === 'pm') hour += 12;
  if (hour > 23 || minute > 59) return null;

  const utcMs = wallTimeInTzToUtcMs(SESSION_TZ, yyyy, mm, dd, hour, minute);
  if (utcMs == null) return null;
  const dt = new Date(utcMs);
  return isNaN(dt.getTime()) ? null : dt;
};

/** Friendly timezone name → IANA identifier. Defaults to ET. */
export const friendlyToIana = (settingsTz) =>
  IANA_BY_FRIENDLY[settingsTz] || SESSION_TZ;

/** IANA identifier → friendly timezone name. Empty string when unknown. */
export const ianaToFriendly = (iana) => {
  for (const [friendly, ianaId] of Object.entries(IANA_BY_FRIENDLY)) {
    if (ianaId === iana) return friendly;
  }
  return '';
};

/**
 * Convert a (calendarDate, hhmm) wall time in `sourceTz` into the equivalent
 * wall time in ET. Returns { date, time } both in YYYY-MM-DD / HH:MM form.
 * If source is already ET, returns the inputs unchanged.
 *
 * @param {string} calendarDate - YYYY-MM-DD in sourceTz
 * @param {string} hhmm         - HH:MM in sourceTz
 * @param {string} sourceTz     - IANA identifier (default ET)
 * @returns {{ date: string, time: string }|null}
 */
export const convertToEt = (calendarDate, hhmm, sourceTz = SESSION_TZ) => {
  if (!calendarDate || !hhmm) return null;
  if (sourceTz === SESSION_TZ) return { date: calendarDate, time: hhmm };

  const [y, m, d] = calendarDate.split('-').map(n => parseInt(n, 10));
  if (!y || !m || !d) return null;
  const mins = parseHM(hhmm);
  if (mins == null) return null;
  const hour = Math.floor(mins / 60);
  const minute = mins % 60;

  const utcMs = wallTimeInTzToUtcMs(sourceTz, y, m, d, hour, minute);
  if (utcMs == null) return null;

  const etParts = getEtParts(new Date(utcMs));
  return {
    date: `${etParts.year}-${etParts.month}-${etParts.day}`,
    time: `${String(etParts.hour).padStart(2, '0')}:${String(etParts.minute).padStart(2, '0')}`,
  };
};

/** Convert any UTC/ms/Date to an ET-anchored Date (for display libraries). */
export const utcToEtParts = getEtParts;

/**
 * Returns the Unix-second timestamp for the session close (5pm ET) of the
 * given trading-day calendar date. Used by the trade-chart to extend the
 * default visible range past the trade's exit so users can see how the
 * session played out after they were flat.
 *
 * @param {string} dateStr  YYYY-MM-DD (the trading day)
 * @returns {number|null}   Unix seconds, or null on bad input
 */
export const getSessionCloseTsSec = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const [y, m, d] = dateStr.split('-').map(n => parseInt(n, 10));
  if (!y || !m || !d) return null;
  const utcMs = wallTimeInTzToUtcMs(
    SESSION_TZ, y, m, d, SESSION_CLOSE_HOUR, 0
  );
  if (!Number.isFinite(utcMs)) return null;
  return Math.floor(utcMs / 1000);
};
